import { useRef, useEffect, useMemo, useCallback } from 'react';
import DocumentTitle from 'react-document-title';
import { useParams } from 'react-router-dom';

import { useBreakpoint } from '@hivebrite/design-system/Theme';
import { useScrollTop } from '@hivebrite/react-hooks';

import MediaCenterBreadcrumb from 'hb-react/frontoffice/apps/MediaCenter/components/Breadcrumb';
import FilesRenderer from 'hb-react/frontoffice/apps/MediaCenter/components/FilesRenderer';
import FolderNotFound from 'hb-react/frontoffice/apps/MediaCenter/components/FolderNotFound';
import MediaCenterFolderPageHeader from 'hb-react/frontoffice/apps/MediaCenter/components/FolderPageHeader';
import FoldersRenderer from 'hb-react/frontoffice/apps/MediaCenter/components/FoldersRenderer';
import {
  FoldersTitle,
  FilesTitle,
} from 'hb-react/frontoffice/apps/MediaCenter/components/ItemsTitles';
import { useFilesAndFoldersId } from 'hb-react/frontoffice/apps/MediaCenter/container/hooks/useFilesAndFoldersId';
import {
  EMPTY_CONTENT_ID,
  FILTERS_ID,
  SORT_BY,
  ITEMS_BY_ROW,
  TAGS_LIMIT,
} from 'hb-react/frontoffice/apps/MediaCenter/container/utils/rendererConstants';
import {
  makeFilters,
  getDataFiltersForFiles,
  getDataFiltersForFolders,
  getMediaCenterHeaderProps,
  makeFetchFiles,
  makeFetchFolders,
  scrollToFilesBlock,
} from 'hb-react/frontoffice/apps/MediaCenter/container/utils/rendererUtils';
import MediaCenterPreviewModal from 'hb-react/frontoffice/apps/MediaCenter/MediaCenterPreviewModal';
import {
  fetchFilesUrl,
  fetchFoldersUrl,
} from 'hb-react/frontoffice/apps/MediaCenter/utils/mediaCenterUrls';
import BlocksRenderer from 'hb-react/frontoffice/components/BlocksRenderer';
import { BLOCKS } from 'hb-react/frontoffice/components/BlocksRenderer/shared';
import { useUIContext } from 'hb-react/frontoffice/contexts/UIContext';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { useAppHeaderVariant } from 'hb-react/shared/components/DesignSystem/AppHeader';
import ThumbnailLoader from 'hb-react/shared/components/Thumbnails/ThumbnailLoader';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import { useMenuName } from 'hb-react/shared/hooks/useMenuName';
import { generatePageTitle } from 'hb-react/shared/utils/generatePageTitle';
import { getPageTitleFromNetwork } from 'hb-react/shared/utils/getPageTitleFromNetwork';

const MediaCenterBlocksRenderer = ({
  configPage,
  currentFolder,
  topic,
  isFolderEmpty,
  isLoadingCurrentFolder,
  tags,
}) => {
  const { flagAnalyticsChangeTitleTags } = useFeatureFlag();
  const { currentNetwork } = useDefaultAppContexts();
  const {
    headerHeight = 0,
    headerHeightMobile = 0,
    topicMenuHeight = 0,
  } = useUIContext();

  const initialFolderSort = SORT_BY.find(
    (element) =>
      element.value ===
      currentNetwork.network_configuration.media_center.default_folder_sort,
  );

  const initialFileSort = SORT_BY.find(
    (element) =>
      element.value ===
      currentNetwork.network_configuration.media_center.default_file_sort,
  );

  const { fileUuid, folderUuid } = useParams();
  const breakpoint = useBreakpoint();

  const appHeaderProps = getMediaCenterHeaderProps({
    appPageConfig: configPage,
  });
  const { variant } = useAppHeaderVariant(appHeaderProps);

  const filesRef = useRef();
  const filterRef = useRef();
  const folderUuidRef = useRef(folderUuid); // folderUuid is kept as a ref to not be lost on opening a file preview

  const { filesId, foldersId } = useFilesAndFoldersId(folderUuidRef);

  useEffect(() => {
    // clear filter when access a folder
    if (folderUuidRef.current) {
      filterRef.current?.clearFilters();
    }
  }, []);

  useScrollTop([folderUuidRef.current]);

  const scrollToFilesBlockOnClick = useCallback(() => {
    const height = breakpoint === 's' ? headerHeightMobile : headerHeight;

    scrollToFilesBlock(folderUuidRef.current, height + topicMenuHeight);
  }, [breakpoint, headerHeight, headerHeightMobile, topicMenuHeight]);

  const filters = useMemo(
    () => makeFilters(scrollToFilesBlockOnClick),
    [scrollToFilesBlockOnClick],
  );

  const fetchFiles = makeFetchFiles(
    fetchFilesUrl({ folderUuid: folderUuidRef.current, currentNetwork, topic }),
  );

  const fetchFolders = makeFetchFolders(
    fetchFoldersUrl({
      folderUuid: folderUuidRef.current,
      currentNetwork,
      breakpoint,
      topic,
    }),
  );

  const topicTabName = useMenuName('media_center', __('Media Center'));

  if (isFolderEmpty) {
    return (
      <BlocksRenderer
        templateProps={{
          variant,
          topicId: topic?.id,
          hasFilters: false,
        }}
        blocks={[
          {
            ...BLOCKS.appHeader,
            attributes: {
              ...appHeaderProps,
            },
          },
          () => <FolderNotFound />,
        ]}
      />
    );
  }

  return (
    <>
      {flagAnalyticsChangeTitleTags && (
        <DocumentTitle
          title={generatePageTitle({
            title: currentFolder?.name || topicTabName,
            networkName: getPageTitleFromNetwork(currentNetwork),
            topicName: topic?.name,
          })}
        />
      )}
      <BlocksRenderer
        templateProps={{
          variant,
          topicId: topic?.id,
          hasFilters: filters?.length > 0,
        }}
        blocks={[
          {
            ...BLOCKS.appHeader,
            attributes: {
              ...appHeaderProps,
            },
          },
          {
            id: FILTERS_ID,
            ref: filterRef,
            component: BLOCKS.filters.component,
            attributes: {
              filters,
            },
          },
          () => (
            <MediaCenterBreadcrumb
              currentFolder={currentFolder}
              isLoading={isLoadingCurrentFolder}
            />
          ),
          () => (
            <MediaCenterFolderPageHeader
              currentFolder={currentFolder}
              isLoading={isLoadingCurrentFolder}
            />
          ),
          {
            id: foldersId,
            component: BLOCKS.items.component,
            attributes: {
              fetchItems: fetchFolders,
              getDataFilters: getDataFiltersForFolders,
              itemLoader: ThumbnailLoader,
              itemRenderer: FoldersRenderer,
              itemsByRow: ITEMS_BY_ROW[breakpoint],
              loaderLines: 1,
              shouldHideIfEmpty: true,
              infiniteLoad: false,
              title: FoldersTitle,
              sortBy: SORT_BY,
              initialSort: initialFolderSort,
              resultsFoundMessage: (itemsSize) =>
                n__(
                  '%{itemsSize} folder found',
                  '%{itemsSize} folders found',
                  itemsSize,
                ).i__({
                  itemsSize: `${itemsSize}`,
                }),
              noResultsMessage: __('No folders found'),
              columnsInMobile: true,
            },
          },
          {
            id: filesId,
            ref: filesRef,
            component: BLOCKS.items.component,
            attributes: {
              fetchItems: fetchFiles,
              getDataFilters: getDataFiltersForFiles,
              itemLoader: ThumbnailLoader,
              itemRenderer: FilesRenderer,
              itemsByRow: ITEMS_BY_ROW[breakpoint],
              loaderLines: 1,
              shouldHideIfEmpty: true,
              infiniteLoad: true,
              tags: {
                tags: tags || [],
                position: 'after-title',
                multi: true,
                limit: TAGS_LIMIT[breakpoint],
              },
              title: FilesTitle,
              sortBy: SORT_BY,
              initialSort: initialFileSort,
              resultsFoundMessage: (itemsSize) =>
                n__(
                  '%{itemsSize} file found',
                  '%{itemsSize} files found',
                  itemsSize,
                ).i__({
                  itemsSize: `${itemsSize}`,
                }),
              noResultsMessage: __('No files found'),
              columnsInMobile: true,
            },
          },
          {
            id: EMPTY_CONTENT_ID,
            component: BLOCKS.emptyContent.component,
            attributes: {
              shouldDisplay: ({ data }) =>
                data?.[filesId]?.length === 0 &&
                data?.[foldersId]?.length === 0 &&
                !filesRef?.current?.checkHasTags() &&
                !data?.[FILTERS_ID],
              title: s__('MediaCenter|No content has been posted yet.'),
            },
          },
        ]}
      />
      {fileUuid && (
        <MediaCenterPreviewModal
          fetchNextPage={filesRef?.current?.fetchNextPage}
          folderUuid={folderUuidRef.current}
          topicName={topic?.name}
        />
      )}
    </>
  );
};

export default MediaCenterBlocksRenderer;
